import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  Download,
  Visibility,
  Delete,
  BarChart,
  CalendarMonth,
  People,
  AttachMoney,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { reportService } from '../services/api';

const ReportsPage = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total_campaigns: 0,
    total_raised: 0,
    total_donors: 0,
    avg_completion: 0,
  });
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [pageNumber, setPageNumber] = useState(1);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [filters, setFilters] = useState({
    report_type: '',
    start_date: '',
    end_date: '',
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  useEffect(() => {
    fetchReports();
    fetchStats();
  }, [pageNumber, filters]);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const params = {
        page: pageNumber,
        limit: 10,
        sort: 'report_date-desc',
        report_type: filters.report_type || undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
      };

      const response = await reportService.getAllReports(params);
      const data = response.data?.data || [];
      const paginationData = response.data?.pagination || pagination;

      setReports(data);
      setPagination(paginationData);
    } catch (error) {
      console.error('Error fetching reports:', error);
      setToast({ open: true, message: 'Error loading reports', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await reportService.getAnalyticsOverview();
      if (response.data && response.data.data && response.data.data.stats) {
        const statsData = response.data.data.stats;
        setStats({
          total_campaigns: parseInt(statsData.total_campaigns) || 0,
          total_raised: parseFloat(statsData.total_raised) || 0,
          total_donors: parseInt(statsData.total_donors) || 0,
          avg_completion: parseFloat(statsData.avg_completion) || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleViewReport = (reportId) => {
    navigate(`/admin/reports/${reportId}`);
  };

  const handleDeleteClick = (reportId) => {
    setDeleteDialog({ open: true, id: reportId });
  };

  const handleConfirmDelete = async () => {
    try {
      await reportService.deleteReport(deleteDialog.id);
      setToast({ open: true, message: 'Report deleted successfully', type: 'success' });
      setDeleteDialog({ open: false, id: null });
      await fetchReports();
    } catch (error) {
      setToast({ open: true, message: 'Error deleting report', type: 'error' });
    }
  };

  const handleExport = async (reportId) => {
    try {
      const response = await reportService.getReportById(reportId);
      const data = response.data.data;

      const csvContent = [
        ['Report Details'],
        ['Campaign', data.campaign_title || 'N/A'],
        ['Report Type', data.report_type],
        ['Report Date', data.report_date],
        [''],
        ['Key Metrics'],
        ['Total Donations', `₹${data.total_donations?.toLocaleString() || 0}`],
        ['Donor Count', data.donor_count],
        ['New Donors', data.new_donors],
        ['Average Donation', `₹${data.average_donation?.toLocaleString() || 0}`],
        ['Recurring Donors', data.recurring_donors],
        ['Completion %', `${data.completion_percentage}%`],
      ]
        .map((row) => row.join(','))
        .join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${reportId}.csv`;
      a.click();
    } catch (error) {
      setToast({ open: true, message: 'Error exporting report', type: 'error' });
    }
  };

  return (
    <Box sx={{ backgroundColor: '#f8fafb', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a202c', mb: 1 }}>
            Reports & Analytics
          </Typography>
          <Typography variant="body2" sx={{ color: '#667085' }}>
            Track campaign performance and donor insights
          </Typography>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
                border: '1px solid #e8ecf1',
                '&:hover': {
                  boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ backgroundColor: '#dcfce7', p: 2, borderRadius: '12px' }}>
                  <BarChart sx={{ color: '#2c5f2d', fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                    Total Campaigns
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2c5f2d', mt: 0.5 }}>
                    {stats.total_campaigns || 0}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
                border: '1px solid #e8ecf1',
                '&:hover': {
                  boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ backgroundColor: '#bfdbfe', p: 2, borderRadius: '12px' }}>
                  <AttachMoney sx={{ color: '#1e40af', fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                    Total Raised
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1e40af', mt: 0.5 }}>
                    ₹{parseFloat(stats.total_raised || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
                border: '1px solid #e8ecf1',
                '&:hover': {
                  boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ backgroundColor: '#fecdd3', p: 2, borderRadius: '12px' }}>
                  <People sx={{ color: '#dc2626', fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                    Total Donors
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#dc2626', mt: 0.5 }}>
                    {parseInt(stats.total_donors || 0).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                p: 3,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
                border: '1px solid #e8ecf1',
                '&:hover': {
                  boxShadow: '0 8px 16px rgba(0,0,0,0.12)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ backgroundColor: '#fed7aa', p: 2, borderRadius: '12px' }}>
                  <TrendingUp sx={{ color: '#b45309', fontSize: 28 }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ color: '#667085', fontWeight: '500' }}>
                    Avg Completion
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#b45309', mt: 0.5 }}>
                    {stats.avg_completion?.toFixed(1) || 0}%
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>

        {/* Filters */}
        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={filters.report_type}
                  onChange={(e) => {
                    setFilters({ ...filters, report_type: e.target.value });
                    setPageNumber(1);
                  }}
                  label="Report Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={filters.start_date}
                onChange={(e) => {
                  setFilters({ ...filters, start_date: e.target.value });
                  setPageNumber(1);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={filters.end_date}
                onChange={(e) => {
                  setFilters({ ...filters, end_date: e.target.value });
                  setPageNumber(1);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                sx={{
                  backgroundColor: '#2c5f2d',
                  '&:hover': { backgroundColor: '#1e4620' },
                  height: '40px',
                }}
                onClick={() => {
                  setFilters({ report_type: '', start_date: '', end_date: '' });
                  setPageNumber(1);
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Card>

        {/* Reports Table */}
        <Card sx={{ border: '1px solid #e8ecf1', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: '600', color: '#1a202c', py: 2 }}>
                        Campaign
                      </TableCell>
                      <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: '600', color: '#1a202c' }} align="right">
                        Donations
                      </TableCell>
                      <TableCell sx={{ fontWeight: '600', color: '#1a202c' }} align="right">
                        Donors
                      </TableCell>
                      <TableCell sx={{ fontWeight: '600', color: '#1a202c' }} align="center">
                        Progress
                      </TableCell>
                      <TableCell sx={{ fontWeight: '600', color: '#1a202c' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reports.length > 0 ? (
                      reports.map((report) => (
                        <TableRow
                          key={report.id}
                          sx={{
                            '&:hover': { backgroundColor: '#f8fafb' },
                            borderBottom: '1px solid #e8ecf1',
                          }}
                        >
                          <TableCell sx={{ fontWeight: '500', color: '#1a202c' }}>
                            {report.campaign_title || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={report.report_type}
                              size="small"
                              variant="outlined"
                              sx={{
                                textTransform: 'capitalize',
                                backgroundColor:
                                  report.report_type === 'daily'
                                    ? '#e0f2fe'
                                    : report.report_type === 'weekly'
                                    ? '#f0fdf4'
                                    : '#fef3c7',
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#667085' }}>
                            {new Date(report.report_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell sx={{ color: '#667085', fontWeight: '500' }} align="right">
                            ₹{report.total_donations?.toLocaleString() || 0}
                          </TableCell>
                          <TableCell sx={{ color: '#667085' }} align="right">
                            {report.donor_count || 0}
                          </TableCell>
                          <TableCell align="center" sx={{ minWidth: '120px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={parseFloat(report.completion_percentage) || 0}
                                sx={{ flex: 1, height: 6, borderRadius: 3 }}
                              />
                              <Typography variant="caption" sx={{ fontWeight: '600', minWidth: '30px' }}>
                                {(parseFloat(report.completion_percentage) || 0).toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Visibility />}
                              onClick={() => handleViewReport(report.id)}
                              sx={{ textTransform: 'none' }}
                            >
                              View
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Download />}
                              onClick={() => handleExport(report.id)}
                              sx={{ textTransform: 'none' }}
                            >
                              Export
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Delete />}
                              onClick={() => handleDeleteClick(report.id)}
                              sx={{ textTransform: 'none' }}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: '#999' }}>
                          No reports found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center', gap: 1 }}>
                  <Button
                    disabled={pageNumber === 1}
                    onClick={() => setPageNumber(pageNumber - 1)}
                  >
                    Previous
                  </Button>
                  <Typography sx={{ py: 1, px: 2 }}>
                    Page {pageNumber} of {pagination.totalPages}
                  </Typography>
                  <Button
                    disabled={pageNumber >= pagination.totalPages}
                    onClick={() => setPageNumber(pageNumber + 1)}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </>
          )}
        </Card>
      </Container>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Delete Report</DialogTitle>
        <DialogContent>Are you sure you want to delete this report?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar open={toast.open} autoHideDuration={6000} onClose={() => setToast({ ...toast, open: false })}>
        <Alert severity={toast.type} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportsPage;
